import { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Flex,
  Icon,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Select,
  Input,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
  Spinner,
  useColorMode,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
} from "@chakra-ui/react";
import {
  FaVideo,
  FaUsers,
  FaCheckCircle,
  FaTimesCircle,
  FaBell,
  FaCoins,
  FaEllipsisV,
  FaDownload,
} from "react-icons/fa";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { useQuery, useMutation, gql } from "@apollo/client";
import { getColor } from "../../brandConfig";

const EVENT_ID = "bb-webinar-business-sovereignty";

const GET_REGISTRATIONS = gql`
  query EventRegistrations($filter: EventRegistrationFilterInput) {
    eventRegistrations(filter: $filter) {
      id
      email
      firstName
      lastName
      phone
      status
      source
      registeredAt
      confirmedAt
      attendedAt
      stayedTillEnd
      referredByEmail
    }
  }
`;

const GET_STATS = gql`
  query EventRegistrationStats($eventId: String!) {
    eventRegistrationStats(eventId: $eventId) {
      totalRegistrations
      confirmed
      attended
      noShow
      cancelled
      pendingConfirmation
    }
  }
`;

const UPDATE_STATUS = gql`
  mutation UpdateRegistrationStatus($input: UpdateRegistrationStatusInput!) {
    updateRegistrationStatus(input: $input) {
      id
      status
    }
  }
`;

const MARK_STAYED = gql`
  mutation MarkStayedTillEnd($id: ID!) {
    markStayedTillEnd(id: $id) {
      id
      stayedTillEnd
    }
  }
`;

const SEND_REMINDERS = gql`
  mutation SendEventReminders($eventId: String!) {
    sendEventReminders(eventId: $eventId)
  }
`;

const TOKEN_ECONOMICS = gql`
  query TokenEconomicsReport {
    tokenEconomicsReport {
      totalIssued
      totalRedeemed
      totalExpired
      totalActive
      tokenLiability
      breakageRate
      issuedThisMonth
      redeemedThisMonth
      redeemedThisMonthValue
    }
  }
`;

const AdminPage = () => {
  const { colorMode } = useColorMode();
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");

  const bg = getColor("background.main", colorMode);
  const cardGradientBg =
    colorMode === "light" ? "rgba(255,255,255,0.95)" : "rgba(20,20,20,0.8)";
  const cardBorder =
    colorMode === "light" ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)";
  const textPrimary = colorMode === "light" ? "gray.800" : "white";
  const textSecondary = colorMode === "light" ? "gray.600" : "gray.300";
  const textMuted = colorMode === "light" ? "gray.500" : "gray.500";

  const { data: regData, loading: regLoading, refetch: refetchRegs } = useQuery(GET_REGISTRATIONS, {
    variables: {
      filter: {
        eventId: EVENT_ID,
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(search ? { search } : {}),
      },
    },
  });

  const { data: statsData, refetch: refetchStats } = useQuery(GET_STATS, {
    variables: { eventId: EVENT_ID },
  });

  const { data: tokenData } = useQuery(TOKEN_ECONOMICS, {
    onError: () => {}, // May not have permissions
  });

  const [updateStatus] = useMutation(UPDATE_STATUS);
  const [markStayed] = useMutation(MARK_STAYED);
  const [sendReminders, { loading: sendingReminders }] = useMutation(SEND_REMINDERS);

  const registrations = regData?.eventRegistrations || [];
  const stats = statsData?.eventRegistrationStats;
  const tokenEcon = tokenData?.tokenEconomicsReport;

  const handleStatusChange = async (id: string, status: string) => {
    try {
      await updateStatus({ variables: { input: { id, status } } });
      toast({ title: `Status updated to ${status}`, status: "success", duration: 2000 });
      refetchRegs();
      refetchStats();
    } catch (error) {
      toast({ title: "Failed to update status", status: "error", duration: 3000 });
    }
  };

  const handleMarkStayed = async (id: string) => {
    try {
      await markStayed({ variables: { id } });
      toast({ title: "Marked as stayed till end + token issued", status: "success", duration: 2000 });
      refetchRegs();
    } catch (error) {
      toast({ title: "Failed to mark", status: "error", duration: 3000 });
    }
  };

  const handleSendReminders = async () => {
    try {
      const { data } = await sendReminders({ variables: { eventId: EVENT_ID } });
      toast({
        title: `Reminders sent to ${data?.sendEventReminders || 0} registrants`,
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      toast({ title: "Failed to send reminders", status: "error", duration: 3000 });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "REGISTERED": return "blue";
      case "CONFIRMED": return "cyan";
      case "REMINDED": return "yellow";
      case "ATTENDED": return "green";
      case "NO_SHOW": return "red";
      case "CANCELLED": return "gray";
      default: return "gray";
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const downloadCSV = () => {
    const headers = ["First Name", "Last Name", "Email", "Phone", "Status", "Source", "Registered At", "Referred By"];
    const rows = registrations.map((r: any) => [
      r.firstName,
      r.lastName || "",
      r.email,
      r.phone || "",
      r.status,
      r.source,
      new Date(r.registeredAt).toISOString(),
      r.referredByEmail || "",
    ]);
    const csv = [headers, ...rows].map((row) => row.map((cell: string) => `"${cell}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${EVENT_ID}-registrations.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box bg={bg} minHeight="100vh" p={6}>
      <Container maxW="1600px">
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
            <Box>
              <Heading size="xl" color={textPrimary}>
                <Icon as={FaVideo} mr={3} color="#9333EA" />
                Live Event Admin
              </Heading>
              <Text color={textSecondary} mt={1}>
                Event: {EVENT_ID}
              </Text>
            </Box>
            <HStack spacing={3}>
              <Button
                leftIcon={<Icon as={FaBell} />}
                colorScheme="purple"
                onClick={handleSendReminders}
                isLoading={sendingReminders}
              >
                Send Reminders
              </Button>
              <Button
                leftIcon={<Icon as={FaDownload} />}
                variant="outline"
                onClick={downloadCSV}
              >
                Export CSV
              </Button>
            </HStack>
          </Flex>

          {/* Stats */}
          {stats && (
            <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4}>
              <Card bg={cardGradientBg} borderWidth="1px" borderColor={cardBorder}>
                <CardBody py={4} textAlign="center">
                  <Stat>
                    <StatLabel color={textMuted}>Total</StatLabel>
                    <StatNumber color={textPrimary}>{stats.totalRegistrations}</StatNumber>
                  </Stat>
                </CardBody>
              </Card>
              <Card bg={cardGradientBg} borderWidth="1px" borderColor={cardBorder}>
                <CardBody py={4} textAlign="center">
                  <Stat>
                    <StatLabel color={textMuted}>Confirmed</StatLabel>
                    <StatNumber color="cyan.500">{stats.confirmed}</StatNumber>
                  </Stat>
                </CardBody>
              </Card>
              <Card bg={cardGradientBg} borderWidth="1px" borderColor={cardBorder}>
                <CardBody py={4} textAlign="center">
                  <Stat>
                    <StatLabel color={textMuted}>Attended</StatLabel>
                    <StatNumber color="green.500">{stats.attended}</StatNumber>
                  </Stat>
                </CardBody>
              </Card>
              <Card bg={cardGradientBg} borderWidth="1px" borderColor={cardBorder}>
                <CardBody py={4} textAlign="center">
                  <Stat>
                    <StatLabel color={textMuted}>No Show</StatLabel>
                    <StatNumber color="red.500">{stats.noShow}</StatNumber>
                  </Stat>
                </CardBody>
              </Card>
              <Card bg={cardGradientBg} borderWidth="1px" borderColor={cardBorder}>
                <CardBody py={4} textAlign="center">
                  <Stat>
                    <StatLabel color={textMuted}>Cancelled</StatLabel>
                    <StatNumber color="gray.500">{stats.cancelled}</StatNumber>
                  </Stat>
                </CardBody>
              </Card>
              <Card bg={cardGradientBg} borderWidth="1px" borderColor={cardBorder}>
                <CardBody py={4} textAlign="center">
                  <Stat>
                    <StatLabel color={textMuted}>Pending</StatLabel>
                    <StatNumber color="blue.500">{stats.pendingConfirmation}</StatNumber>
                  </Stat>
                </CardBody>
              </Card>
            </SimpleGrid>
          )}

          {/* Token Economics (Admin) */}
          {tokenEcon && (
            <Card bg="rgba(147, 51, 234, 0.05)" borderWidth="1px" borderColor="rgba(147, 51, 234, 0.2)" borderRadius="xl">
              <CardHeader pb={2}>
                <Flex align="center" gap={2}>
                  <Icon as={FaCoins} color="#F59E0B" />
                  <Heading size="md" color={textPrimary}>Token Economics</Heading>
                </Flex>
              </CardHeader>
              <CardBody>
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                  <Box>
                    <Text fontSize="xs" color={textMuted} textTransform="uppercase">Total Issued</Text>
                    <Text fontSize="xl" fontWeight="700" color={textPrimary}>{tokenEcon.totalIssued}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color={textMuted} textTransform="uppercase">Active</Text>
                    <Text fontSize="xl" fontWeight="700" color="green.500">{tokenEcon.totalActive}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color={textMuted} textTransform="uppercase">Token Liability</Text>
                    <Text fontSize="xl" fontWeight="700" color="#F59E0B">AU${tokenEcon.tokenLiability.toLocaleString()}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color={textMuted} textTransform="uppercase">Breakage Rate</Text>
                    <Text fontSize="xl" fontWeight="700" color={textPrimary}>{tokenEcon.breakageRate.toFixed(1)}%</Text>
                  </Box>
                </SimpleGrid>
              </CardBody>
            </Card>
          )}

          {/* Filters */}
          <Flex gap={4} flexWrap="wrap">
            <Input
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              maxW="300px"
              borderColor={cardBorder}
            />
            <Select
              placeholder="All Statuses"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              maxW="200px"
              borderColor={cardBorder}
            >
              <option value="REGISTERED">Registered</option>
              <option value="CONFIRMED">Confirmed</option>
              <option value="REMINDED">Reminded</option>
              <option value="ATTENDED">Attended</option>
              <option value="NO_SHOW">No Show</option>
              <option value="CANCELLED">Cancelled</option>
            </Select>
          </Flex>

          {/* Registrations Table */}
          <Card bg={cardGradientBg} borderWidth="1px" borderColor={cardBorder} borderRadius="xl" overflowX="auto">
            <CardBody p={0}>
              {regLoading ? (
                <Flex justify="center" py={10}>
                  <Spinner size="xl" color="#9333EA" />
                </Flex>
              ) : (
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th color={textMuted}>Name</Th>
                      <Th color={textMuted}>Email</Th>
                      <Th color={textMuted}>Phone</Th>
                      <Th color={textMuted}>Status</Th>
                      <Th color={textMuted}>Source</Th>
                      <Th color={textMuted}>Registered</Th>
                      <Th color={textMuted}>Stayed</Th>
                      <Th color={textMuted}>Referred By</Th>
                      <Th color={textMuted}>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {registrations.map((reg: any) => (
                      <Tr key={reg.id}>
                        <Td color={textPrimary} fontWeight="600">
                          {reg.firstName} {reg.lastName || ""}
                        </Td>
                        <Td color={textSecondary}>{reg.email}</Td>
                        <Td color={textSecondary}>{reg.phone || "-"}</Td>
                        <Td>
                          <Badge colorScheme={getStatusColor(reg.status)} borderRadius="full" px={3}>
                            {reg.status}
                          </Badge>
                        </Td>
                        <Td color={textSecondary} fontSize="xs">{reg.source}</Td>
                        <Td color={textSecondary} fontSize="xs">{formatDate(reg.registeredAt)}</Td>
                        <Td>
                          {reg.stayedTillEnd ? (
                            <Icon as={FaCheckCircle} color="green.500" />
                          ) : (
                            <Icon as={FaTimesCircle} color="gray.400" />
                          )}
                        </Td>
                        <Td color={textSecondary} fontSize="xs">{reg.referredByEmail || "-"}</Td>
                        <Td>
                          <Menu>
                            <MenuButton as={IconButton} icon={<FaEllipsisV />} variant="ghost" size="sm" />
                            <MenuList>
                              <MenuItem onClick={() => handleStatusChange(reg.id, "ATTENDED")}>
                                Mark Attended
                              </MenuItem>
                              <MenuItem onClick={() => handleStatusChange(reg.id, "NO_SHOW")}>
                                Mark No Show
                              </MenuItem>
                              <MenuItem onClick={() => handleMarkStayed(reg.id)}>
                                Mark Stayed Till End
                              </MenuItem>
                              <MenuItem onClick={() => handleStatusChange(reg.id, "CANCELLED")}>
                                Cancel Registration
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}

              {!regLoading && registrations.length === 0 && (
                <Flex justify="center" py={10}>
                  <Text color={textMuted}>No registrations found</Text>
                </Flex>
              )}
            </CardBody>
          </Card>
        </VStack>
      </Container>
    </Box>
  );
};

export default AdminPage;
